import { useEffect, useMemo, useState } from 'react';
import type { Finding, FindingStatus, FindingsListResponse } from '../types';
import { listFindings } from '../lib/api';
import { IconRefresh, IconSearch, IconChevronRight } from '../components/icons';
import { SkeletonRows } from '../components/Skeleton';
import { useToast } from '../lib/toast';
import { FindingEditDrawer } from './FindingEditDrawer';
import { relativeDate } from '../lib/format';

interface Props {
  /** Remonté à App.tsx pour piloter le pip Veille en sidebar (pending cran 3). */
  onPendingHighCranChange?: (n: number) => void;
  /** Total findings, pour le compteur sidebar. */
  onCountChange?: (n: number) => void;
}

const STATUS_FILTERS: ('all' | FindingStatus)[] = [
  'all', 'pending', 'needs-edit', 'auto-applied', 'approved', 'rejected',
];

const CRAN_FILTERS: ('all' | '1' | '2' | '3')[] = ['all', '3', '2', '1'];

const STATUS_STYLE: Record<FindingStatus, string> = {
  pending: 'bg-amber-soft text-amber',
  'auto-applied': 'bg-cream-deep text-navy',
  approved: 'bg-funds-soft text-funds-deep',
  rejected: 'bg-red-50 text-red-700',
  'needs-edit': 'bg-founders-soft text-founders-deep',
};

const CRAN_STYLE: Record<string, string> = {
  '1': 'bg-cream-tint text-ink-muted',
  '2': 'bg-founders-soft text-founders-deep',
  '3': 'bg-amber-soft text-amber',
};

const KIND_LABEL: Record<string, string> = {
  'new-program': 'Nouveau prog.',
  'deadline-changed': 'Deadline',
  'amount-changed': 'Montant',
  'eligibility-changed': 'Éligibilité',
  'link-broken': 'Lien cassé',
  'new-call': 'Nouvel appel',
  other: 'Autre',
};

/**
 * Page Veille (PR 5). Worklist priorisée des findings du watcher hebdomadaire.
 *
 * Tri serveur : pending d'abord, puis needs-edit, puis cran décroissant
 * (cran 3 plus prioritaire que cran 1). Filtres client side sur status et
 * cran. Le drawer permet review (cran/status/notes) + action Promote pour
 * les findings kind=new-program.
 *
 * Pip sidebar : gold si pending cran 3 > 0 (signal d'urgence éditoriale).
 */
export function VeillePage({ onCountChange, onPendingHighCranChange }: Props) {
  const [findings, setFindings] = useState<Finding[]>([]);
  const [byStatus, setByStatus] = useState<FindingsListResponse['byStatus'] | null>(null);
  const [byCran, setByCran] = useState<FindingsListResponse['byCran'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Finding | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | FindingStatus>('pending');
  const [filterCran, setFilterCran] = useState<'all' | '1' | '2' | '3'>('all');
  const toast = useToast();

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await listFindings();
      setFindings(res.findings);
      setByStatus(res.byStatus);
      setByCran(res.byCran);
      onCountChange?.(res.count);
      // Pip pending cran 3 = signal d'urgence (à reviewer)
      const pendingHighCran = res.findings.filter(
        (f) => f.status === 'pending' && f.cran === '3'
      ).length;
      onPendingHighCranChange?.(pendingHighCran);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load findings');
      setFindings([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return findings.filter((f) => {
      if (q) {
        const blob = `${f.findingId ?? ''} ${f.field ?? ''} ${f.evidence ?? ''} ${f.before ?? ''} ${f.after ?? ''} ${f.kind ?? ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (filterStatus !== 'all' && f.status !== filterStatus) return false;
      if (filterCran !== 'all' && f.cran !== filterCran) return false;
      return true;
    });
  }, [findings, search, filterStatus, filterCran]);

  function onSaved(updated: Finding) {
    setFindings((list) => list.map((f) => (f.id === updated.id ? updated : f)));
    setEditing(updated);
    // Recalcule les agrégats pour le pip
    refreshAggregates();
  }

  function onPromoted(updated: Finding, programmeId: string) {
    setFindings((list) => list.map((f) => (f.id === updated.id ? updated : f)));
    setEditing(null);
    toast.success('Programme créé', `Voir ND - Programmes / ${programmeId}`);
    refreshAggregates();
  }

  function onDeleted(id: string) {
    setFindings((list) => {
      const next = list.filter((f) => f.id !== id);
      onCountChange?.(next.length);
      return next;
    });
    setEditing(null);
    toast.success('Finding supprimé', '');
    refreshAggregates();
  }

  function refreshAggregates() {
    // Recalcule pendingHighCran sans refaire un fetch (optimiste).
    const pendingHighCran = findings.filter(
      (f) => f.status === 'pending' && f.cran === '3'
    ).length;
    onPendingHighCranChange?.(pendingHighCran);
  }

  return (
    <div className="px-4 md:px-8 py-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-display tracking-tightish text-ink">
            Veille
          </h1>
          <p className="mt-1 text-[13.5px] text-ink-muted max-w-2xl">
            Worklist des findings du watcher hebdomadaire (Claude scheduled). Tri par priorité :
            pending d&apos;abord, puis cran 3 &gt; 2 &gt; 1. Click une carte pour reviewer ou promouvoir.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            className="h-9 px-3 rounded-md border border-line bg-white text-[13px] text-ink-muted hover:bg-cream-tint transition disabled:opacity-50 flex items-center gap-1.5"
          >
            <IconRefresh className={'w-4 h-4 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          <span className="font-medium">Erreur : </span>{error}
        </div>
      )}

      {/* KPIs */}
      {byStatus && byCran && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <Kpi label="Pending" value={byStatus.pending} accent="amber" />
          <Kpi label="Needs edit" value={byStatus['needs-edit']} accent="founders" />
          <Kpi label="Auto-applied" value={byStatus['auto-applied']} accent="navy" />
          <Kpi label="Approved" value={byStatus.approved} accent="funds" />
          <Kpi label="Rejected" value={byStatus.rejected} accent="red" />
        </div>
      )}

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md border border-line-soft bg-cream p-3 shadow-card">
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">Recherche</label>
          <div className="relative">
            <IconSearch className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Finding ID, field, evidence..."
              className="h-9 w-full pl-8 pr-3 rounded-md border border-line bg-white text-[13px]"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">Status</label>
          <div className="flex gap-1">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={
                  'px-2.5 py-1.5 rounded-md text-[11.5px] font-medium border transition ' +
                  (filterStatus === s
                    ? 'bg-navy text-white border-navy'
                    : 'bg-white text-ink-muted border-line hover:bg-cream-tint')
                }
              >
                {s === 'all' ? 'Tous' : s}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">Cran</label>
          <div className="flex gap-1">
            {CRAN_FILTERS.map((c) => (
              <button
                key={c}
                onClick={() => setFilterCran(c)}
                className={
                  'h-9 w-9 grid place-items-center rounded-md text-[12px] font-medium border transition ' +
                  (filterCran === c
                    ? 'bg-navy text-white border-navy'
                    : 'bg-white text-ink-muted border-line hover:bg-cream-tint')
                }
              >
                {c === 'all' ? '*' : c}
              </button>
            ))}
          </div>
        </div>
        <div className="ml-auto text-[12px] text-ink-muted self-end">
          <span className="num">{filtered.length}</span> sur <span className="num">{findings.length}</span>
        </div>
      </div>

      {/* Liste worklist */}
      <div className="rounded-md border border-line-soft bg-cream shadow-card overflow-hidden">
        {loading && findings.length === 0 ? (
          <div className="p-3"><SkeletonRows rows={6} cols={5} /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-[13.5px] text-ink font-medium">
              {findings.length === 0
                ? 'Aucun finding pour le moment.'
                : 'Aucun finding pour ces filtres.'}
            </p>
            <p className="mt-1 text-[12px] text-ink-muted">
              {findings.length === 0
                ? 'Le watcher Claude écrit dans cette table chaque semaine. Si elle est vide, lance non-dilutive-monitor manuellement ou attends le prochain cron.'
                : 'Élargis les filtres ou clique sur Tous.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-line-soft">
            {filtered.map((f) => (
              <FindingRow key={f.id} finding={f} onClick={() => setEditing(f)} />
            ))}
          </ul>
        )}
      </div>

      {/* Drawer */}
      {editing && (
        <FindingEditDrawer
          finding={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
          onDeleted={onDeleted}
          onPromoted={onPromoted}
        />
      )}
    </div>
  );
}

function FindingRow({ finding: f, onClick }: { finding: Finding; onClick: () => void }) {
  return (
    <li>
      <button onClick={onClick} className="w-full text-left px-3 py-2.5 hover:bg-cream-tint transition flex items-center gap-3 group">
        {/* Cran badge */}
        <span className={'shrink-0 h-7 w-7 grid place-items-center rounded-md text-[12px] font-bold ' + (CRAN_STYLE[f.cran || ''] || 'bg-cream-tint text-ink-muted')}>
          {f.cran || '?'}
        </span>
        {/* Status badge */}
        <span className={'shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-medium ' + (STATUS_STYLE[f.status || 'pending'] || 'bg-cream-tint text-ink-muted')}>
          {f.status || 'pending'}
        </span>
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[12.5px] font-medium text-ink truncate">
              {KIND_LABEL[f.kind || ''] || f.kind || 'inconnu'}
            </span>
            {f.field && (
              <span className="text-[11px] font-mono text-ink-faint">on {f.field}</span>
            )}
            {f.findingId && (
              <span className="text-[10.5px] font-mono text-ink-faint">{f.findingId}</span>
            )}
          </div>
          {(f.before || f.after) && (
            <div className="mt-0.5 text-[11.5px] text-ink-muted truncate">
              {f.before ? `${f.before.slice(0, 60)}` : ''} {f.before && f.after ? '→' : ''} {f.after ? f.after.slice(0, 60) : ''}
            </div>
          )}
          {f.evidence && !f.before && (
            <div className="mt-0.5 text-[11.5px] text-ink-muted italic truncate">{f.evidence.slice(0, 100)}</div>
          )}
        </div>
        <div className="shrink-0 text-[11px] text-ink-faint">
          {f.runAt ? relativeDate(f.runAt) : ''}
        </div>
        <IconChevronRight className="w-3 h-3 text-ink-faint opacity-0 group-hover:opacity-100 transition" />
      </button>
    </li>
  );
}

function Kpi({ label, value, accent }: { label: string; value: number; accent: 'amber' | 'founders' | 'navy' | 'funds' | 'red' }) {
  const accentClass = {
    amber: 'text-amber',
    founders: 'text-founders-deep',
    navy: 'text-navy',
    funds: 'text-funds-deep',
    red: 'text-red-700',
  }[accent];
  return (
    <div className="rounded-md border border-line-soft bg-cream p-3 shadow-card">
      <p className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">{label}</p>
      <p className={'text-[22px] font-display tracking-tightish mt-1 num ' + accentClass}>{value}</p>
    </div>
  );
}
